import type {
  DiagnosisEnergyUseReadDTO,
  DiagnosisEnergyVectorsReadDTO,
  DiagnosisInterventionsReadDTO,
  IDiagnosisInterventionsCreateDTO,
} from '../generated/api-client';

/** Chiave di matching vettore: stessa logica concettuale del backend (code → table_code_index → tipo+udm). */
export function matchVectorKey(v: DiagnosisEnergyVectorsReadDTO): string {
  const code = (v.code || '').trim();
  if (code) return `code:${code}`;
  const tci = (v.table_code_index || '').trim();
  if (tci) return `tci:${tci}`;
  return `vt:${v.vector_type || ''}|uom:${v.unit_of_measurement || ''}`;
}

/** Match vettore come il backend: code → table_code_index → (vector_type, unit_of_measurement). */
function findTargetVectorId(
  source: DiagnosisEnergyVectorsReadDTO,
  targetVectors: DiagnosisEnergyVectorsReadDTO[]
): string | undefined {
  const sCode = (source.code || '').trim();
  const sTci = (source.table_code_index || '').trim();
  const sType = source.vector_type || '';
  const sUom = source.unit_of_measurement || '';
  if (sCode) {
    const t = targetVectors.find(v => (v.code || '').trim() === sCode);
    if (t?.id) return t.id;
  }
  if (sTci) {
    const t = targetVectors.find(v => (v.table_code_index || '').trim() === sTci);
    if (t?.id) return t.id;
  }
  const t = targetVectors.find(
    v => (v.vector_type || '') === sType && (v.unit_of_measurement || '') === sUom
  );
  return t?.id;
}

export function buildVectorIdMap(
  sourceVectors: DiagnosisEnergyVectorsReadDTO[],
  targetVectors: DiagnosisEnergyVectorsReadDTO[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const s of sourceVectors) {
    if (!s.id) continue;
    const targetId = findTargetVectorId(s, targetVectors);
    if (targetId) map.set(s.id, targetId);
  }
  return map;
}

function findTargetEnergyUseId(
  source: DiagnosisEnergyUseReadDTO,
  targetUses: DiagnosisEnergyUseReadDTO[]
): string | undefined {
  const sCode = (source.code || '').trim();
  if (sCode) {
    const t = targetUses.find(u => (u.code || '').trim() === sCode);
    if (t?.id) return t.id;
  }
  const sDesc = (source.description || '').trim();
  if (sDesc) {
    const t = targetUses.find(u => (u.description || '').trim() === sDesc);
    if (t?.id) return t.id;
  }
  return undefined;
}

export function buildEnergyUseIdMap(
  sourceUses: DiagnosisEnergyUseReadDTO[],
  targetUses: DiagnosisEnergyUseReadDTO[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const s of sourceUses) {
    if (!s.id) continue;
    const targetId = findTargetEnergyUseId(s, targetUses);
    if (targetId) map.set(s.id, targetId);
  }
  return map;
}

export type InterventionGridRow = DiagnosisInterventionsReadDTO & {
  _raw?: DiagnosisInterventionsReadDTO;
  [key: string]: unknown;
};

/**
 * Righe griglia con colonne consumo/risparmio allineate agli id vettore della diagnosi **target**
 * (colonne = `energyVectors`). Se `vectorIdMap` è null, si assume stessa diagnosi (chiavi già coerenti).
 */
export function buildInterventionGridRows(
  interventions: DiagnosisInterventionsReadDTO[],
  energyVectors: DiagnosisEnergyVectorsReadDTO[],
  vectorIdMap: Map<string, string> | null,
  energyUseIdMap: Map<string, string> | null
): InterventionGridRow[] {
  return interventions.map(i => {
    const cMap = i.consumption_before_by_vector ?? {};
    const sMap = i.annual_energy_saving_by_vector ?? {};
    const tMap = i.to_evaluated_by_vector ?? {};
    const extra: Record<string, unknown> = {};
    const newToEval: Record<string, boolean> = {};

    energyVectors.forEach(v => {
      const vid = v.id!;
      if (!vectorIdMap) {
        extra[`consumption_before_${vid}`] = cMap[vid] ?? null;
        const te = !!tMap[vid];
        if (te) newToEval[vid] = true;
        extra[`annual_energy_saving_${vid}`] = te ? null : (sMap[vid] ?? null);
      } else {
        let consumption: number | null | undefined = null;
        let saving: number | null | undefined;
        let toEval = false;
        vectorIdMap.forEach((newId, oldId) => {
          if (newId !== vid) return;
          if (cMap[oldId] != null) consumption = cMap[oldId] ?? null;
          if (tMap[oldId]) toEval = true;
          else if (sMap[oldId] != null) saving = sMap[oldId] ?? null;
        });
        extra[`consumption_before_${vid}`] = consumption;
        if (toEval) newToEval[vid] = true;
        extra[`annual_energy_saving_${vid}`] = toEval ? null : (saving ?? null);
      }
    });

    extra['cost_before'] = i.cost_before ?? null;
    extra['to_evaluated_by_vector'] = newToEval;

    const displayEnergyVectors =
      vectorIdMap != null
        ? Array.from(
            new Set(
              (i.energy_vectors ?? [])
                .map(oid => (oid ? vectorIdMap.get(oid) : undefined) ?? oid)
                .filter((x): x is string => !!x)
            )
          )
        : i.energy_vectors;

    const displayEnergyUses =
      energyUseIdMap != null && !i.all_energy_use
        ? Array.from(
            new Set(
              (i.energy_uses ?? [])
                .map(oid => (oid ? energyUseIdMap.get(oid) : undefined) ?? oid)
                .filter((x): x is string => !!x)
            )
          )
        : i.energy_uses;

    return {
      ...i,
      _raw: i,
      energy_vectors: displayEnergyVectors,
      energy_uses: displayEnergyUses,
      ...extra,
    } as InterventionGridRow;
  });
}

/**
 * Costruisce il body POST per copiare un intervento nella diagnosi corrente.
 * Ritorna `null` se non è possibile mappare almeno un vettore richiesto dall'intervento sorgente.
 */
export function buildInterventionCloneCreateDto(
  intervention: DiagnosisInterventionsReadDTO,
  targetDiagnosisId: string,
  vectorIdMap: Map<string, string>,
  energyUseIdMap: Map<string, string>
): IDiagnosisInterventionsCreateDTO | null {
  const srcVectors = intervention.energy_vectors ?? [];
  const mappedVectors = srcVectors
    .map(id => (id ? vectorIdMap.get(id) : undefined))
    .filter((x): x is string => !!x);
  if (srcVectors.length > 0 && mappedVectors.length === 0) return null;

  let mappedUses: string[] = [];
  if (intervention.all_energy_use) {
    mappedUses = [];
  } else {
    const srcUses = intervention.energy_uses ?? [];
    mappedUses = srcUses
      .map(id => (id ? energyUseIdMap.get(id) : undefined))
      .filter((x): x is string => !!x);
    if (srcUses.length > 0 && mappedUses.length === 0) return null;
  }

  const sMap = intervention.annual_energy_saving_by_vector ?? {};
  const tMap = intervention.to_evaluated_by_vector ?? {};
  const newAnnual: { [key: string]: number } = {};
  const newToEval: { [key: string]: boolean } = {};
  for (const [oldVid, val] of Object.entries(sMap)) {
    const nv = vectorIdMap.get(oldVid);
    if (nv && val != null) newAnnual[nv] = val;
  }
  for (const [oldVid, flag] of Object.entries(tMap)) {
    const nv = vectorIdMap.get(oldVid);
    if (nv && flag) newToEval[nv] = true;
  }

  const dto: IDiagnosisInterventionsCreateDTO = {
    diagnosis_id: targetDiagnosisId,
    energy_vectors: mappedVectors,
    code: intervention.code,
    title: intervention.title,
    consumption_before: intervention.consumption_before,
    cost_before_ea: intervention.cost_before_ea,
    energy_uses: intervention.all_energy_use ? [] : mappedUses,
    all_energy_use: intervention.all_energy_use ?? false,
    description: intervention.description,
    type: intervention.type,
    incentives: intervention.incentives,
    priority: intervention.priority,
    comments: intervention.comments,
    investment: intervention.investment,
    investment_to_evaluated: intervention.investment_to_evaluated,
    unit_of_measurement: intervention.unit_of_measurement,
    annual_co2_saving: intervention.annual_co2_saving,
    expected_economic_saving: intervention.expected_economic_saving,
    expected_economic_saving_manual: intervention.expected_economic_saving_manual,
    useful_life: intervention.useful_life,
    payback_time: intervention.payback_time,
    payback_time_manual: intervention.payback_time_manual,
    van_5_10_years: intervention.van_5_10_years,
    van_5_10_years_manual: intervention.van_5_10_years_manual,
    tir_10_years: intervention.tir_10_years,
    tir_10_years_manual: intervention.tir_10_years_manual,
    is_to_export: intervention.is_to_export ?? false,
    annual_energy_saving_by_vector: Object.keys(newAnnual).length ? newAnnual : undefined,
    to_evaluated_by_vector: Object.keys(newToEval).length ? newToEval : undefined,
  };
  return dto;
}
